import React, { useMemo, useCallback } from "react";
import { Box, Typography, Select, MenuItem, FormControl } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { SavingsRealizationData } from "../../../types/contractDashboard";
import styles from "./savingsRealizationRate.module.scss";

interface SavingsRealizationRateProps {
  data: SavingsRealizationData[];
}

// Format large numbers with K/M suffix
const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

// Custom tooltip component for better UX
const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.96)",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "12px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, marginBottom: "8px", color: "#1a1a1a" }}
      >
        {label}
      </Typography>
      {payload.map((entry, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: index === payload.length - 1 ? 0 : "4px",
          }}
        >
          <Box
            sx={{
              width: "12px",
              height: "12px",
              borderRadius: "2px",
              backgroundColor: entry.color,
            }}
          />
          <Typography variant="body2" sx={{ color: "#666", fontSize: "13px" }}>
            {entry.name}:
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#1a1a1a", fontSize: "13px" }}
          >
            {formatCurrency(Number(entry.value))}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const SavingsRealizationRate: React.FC<SavingsRealizationRateProps> = ({
  data,
}) => {
  // Memoize chart data to prevent unnecessary recalculations
  const chartData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      // Add calculated variance for potential use
      variance: item.actualSavings - item.forecastSavings,
    }));
  }, [data]);

  // Memoize Y-axis tick formatter
  const yAxisTickFormatter = useCallback((value: number) => {
    return formatCurrency(value);
  }, []);

  // Memoize max value calculation for better Y-axis scaling
  const maxValue = useMemo(() => {
    if (!chartData || chartData.length === 0) return 0;
    const values = chartData.flatMap((item) => [
      item.actualSavings,
      item.forecastSavings,
    ]);
    return Math.max(...values);
  }, [chartData]);

  // Calculate Y-axis domain with padding
  const yAxisDomain = useMemo(() => {
    return [0, Math.ceil(maxValue * 1.1)];
  }, [maxValue]);

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Box className={styles.titleSection}>
            <Typography variant="h6" className={styles.title}>
              Savings Realization Rate
            </Typography>
            <Typography variant="body2" className={styles.subtitle}>
              Actual realized savings vs negotiated/forecast savings.
            </Typography>
          </Box>
        </Box>
        <Box className={styles.chartPlaceholder}>
          <Typography variant="body2" className={styles.placeholderText}>
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            Savings Realization Rate
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            Actual realized savings vs negotiated/forecast savings.
          </Typography>
        </Box>
        <FormControl className={styles.filter}>
          <Select
            defaultValue="Last 12 Months"
            IconComponent={KeyboardArrowDownIcon}
            className={styles.select}
            aria-label="Time range filter"
          >
            <MenuItem value="Last 12 Months">Last 12 Months</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box className={styles.chartPlaceholder}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              tickFormatter={yAxisTickFormatter}
              domain={yAxisDomain}
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{
                paddingTop: "10px",
                fontSize: "13px",
              }}
              iconType="line"
            />
            <Line
              type="monotone"
              dataKey="forecastSavings"
              stroke="#3b82f6"
              strokeWidth={2}
              name="Forecast Savings"
              animationDuration={800}
              dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 0 }}
            />
            <Line
              type="monotone"
              dataKey="actualSavings"
              stroke="#10b981"
              strokeWidth={2}
              name="Actual Savings"
              animationDuration={800}
              dot={{ fill: "#10b981", strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 0 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default React.memo(SavingsRealizationRate);
